import { useEffect, useRef, useState } from "react";

interface UseCountUpOptions {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  startOnView?: boolean;
}

export function useCountUp({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
  decimals = 0,
  startOnView = true,
}: UseCountUpOptions) {
  const [display, setDisplay] = useState(`${prefix}${(0).toFixed(decimals)}${suffix}`);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!startOnView) {
      startCount();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
          startCount();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, hasStarted]);

  function startCount() {
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;
      setDisplay(`${prefix}${current.toFixed(decimals)}${suffix}`);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(`${prefix}${end.toFixed(decimals)}${suffix}`);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }

  return { ref, display };
}

/**
 * Parse a formatted string like "2.4M", "$12M+", "98%", "8.5%", "200+"
 * Returns { value: number, prefix: string, suffix: string, decimals: number }
 */
export function parseStatValue(raw: string) {
  const prefix = raw.startsWith("$") ? "$" : "";
  const stripped = raw.replace(/^\$/, "");

  let multiplier = 1;
  let suffix = "";

  if (stripped.endsWith("M+")) {
    multiplier = 1;
    suffix = "M+";
  } else if (stripped.endsWith("M")) {
    multiplier = 1;
    suffix = "M";
  } else if (stripped.endsWith("K")) {
    multiplier = 1;
    suffix = "K";
  } else if (stripped.endsWith("%")) {
    suffix = "%";
  } else if (stripped.endsWith("+")) {
    suffix = "+";
  }

  const numStr = stripped.replace(/[MK%+]/g, "");
  const value = parseFloat(numStr) * multiplier;
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;

  return { value, prefix, suffix, decimals };
}
